import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Download,
  FileSpreadsheet,
  FolderLock,
  Key,
  PlusCircle,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import * as XLSX from "xlsx";
import { api, type ApiCategory, type ApiUser } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

export default function AdminUsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ username: "", password: "", displayName: "", isAdmin: false });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<number | null>(null);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [accessUser, setAccessUser] = useState<ApiUser | null>(null);

  function load() {
    setLoading(true);
    Promise.all([api.listUsers(), api.listCategories()])
      .then(([u, c]) => {
        setUsers(u.users);
        setCategories(c.categories);
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  const allSelectableIds = useMemo(
    () => users.filter((u) => me?.id !== u.id).map((u) => u.id),
    [users, me]
  );
  const allSelected =
    allSelectableIds.length > 0 && allSelectableIds.every((id) => selected.has(id));

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(allSelectableIds));
  }
  function toggleOne(id: number) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function clearSelection() {
    setSelected(new Set());
  }

  async function add() {
    setErr(null);
    if (!form.username || !form.password) { setErr("用户名和密码必填"); return; }
    if (form.password.length < 6) { setErr("密码至少 6 位"); return; }
    setSaving(true);
    try {
      await api.createUser(form.username.trim(), form.password, form.isAdmin, form.displayName.trim() || undefined);
      setForm({ username: "", password: "", displayName: "", isAdmin: false });
      load();
    } catch (e) { setErr((e as Error).message); }
    finally { setSaving(false); }
  }

  async function saveDisplayName(u: ApiUser) {
    const next = editingNameValue.trim();
    if ((next || null) === (u.displayName || null)) {
      setEditingName(null);
      return;
    }
    try {
      await api.updateUserDisplayName(u.id, next.length > 0 ? next : null);
      setEditingName(null);
      load();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  async function resetPwd(u: ApiUser) {
    const pwd = window.prompt(`重置 ${u.username} 的密码（至少 6 位）`, "");
    if (!pwd) return;
    if (pwd.length < 6) { alert("密码至少 6 位"); return; }
    setResetting(u.id);
    try {
      await api.resetPassword(u.id, pwd);
      alert(`已重置 ${u.username} 的密码`);
    } catch (e) { alert((e as Error).message); }
    finally { setResetting(null); }
  }

  async function del(u: ApiUser) {
    if (!confirm(`确认删除用户 ${u.username}？此操作不可撤销。`)) return;
    try {
      await api.deleteUser(u.id);
      load();
    } catch (e) { alert((e as Error).message); }
  }

  async function bulkDelete() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!confirm(`确认删除选中的 ${ids.length} 个用户？此操作不可撤销。`)) return;
    try {
      const r = await api.bulkDeleteUsers(ids);
      const failed = r.results.filter((x) => !x.ok);
      const msg =
        `共 ${r.total} 个，成功 ${r.ok}，失败 ${r.failed}` +
        (failed.length
          ? `\n失败：\n` + failed.map((x) => `· ${x.username || x.id}：${x.error}`).join("\n")
          : "");
      alert(msg);
      clearSelection();
      load();
    } catch (e) { alert((e as Error).message); }
  }

  async function bulkReset() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const pwd = window.prompt(`将选中的 ${ids.length} 个用户密码重置为（至少 6 位）：`, "");
    if (!pwd) return;
    if (pwd.length < 6) { alert("密码至少 6 位"); return; }
    if (!confirm(`确认把 ${ids.length} 个用户的密码统一重置为「${pwd}」？`)) return;
    try {
      const r = await api.bulkResetPassword(ids, pwd);
      const failed = r.results.filter((x) => !x.ok);
      const msg =
        `共 ${r.total} 个，成功 ${r.ok}，失败 ${r.failed}` +
        (failed.length
          ? `\n失败：\n` + failed.map((x) => `· ${x.username || x.id}：${x.error}`).join("\n")
          : "");
      alert(msg);
      clearSelection();
    } catch (e) { alert((e as Error).message); }
  }

  function bulkExport() {
    const ids = selected.size > 0 ? selected : new Set(users.map((u) => u.id));
    const rows = users
      .filter((u) => ids.has(u.id))
      .map((u) => ({
        用户名: u.username,
        姓名: u.displayName || "",
        是否管理员: u.is_admin ? "是" : "否",
        创建时间: u.created_at || "",
        最近登录: u.last_login_at || "",
      }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "用户");
    const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "");
    XLSX.writeFile(wb, `mvg-users-${ts}.xlsx`);
  }

  return (
    <>
      <div className="card mb-6 p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-ink-900">新增用户</div>
          <button
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-surface-border bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-surface-muted"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> 从 Excel 批量导入
          </button>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <label className="flex-1">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">用户名</span>
            <input className="input" placeholder="student-002" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </label>
          <label className="flex-1">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">姓名（可选）</span>
            <input className="input" placeholder="Eric Xu" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          </label>
          <label className="flex-1">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-600">初始密码</span>
            <input className="input" placeholder="至少 6 位" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </label>
          <label className="flex items-center gap-2 text-xs text-ink-600 md:pb-2.5">
            <input type="checkbox" checked={form.isAdmin} onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })} />
            管理员
          </label>
          <button className="btn-primary md:w-auto" onClick={add} disabled={saving}>
            <PlusCircle className="h-4 w-4" /> {saving ? "添加中…" : "添加"}
          </button>
        </div>
        <p className="mt-3 text-[11px] text-ink-400">
          密码以 bcrypt 哈希存储，无法读取。请通过安全渠道告知用户初始密码。
        </p>
      </div>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {err}
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-brand/30 bg-brand-light/50 px-4 py-2.5 text-sm">
          <span className="font-semibold text-ink-900">已选 {selected.size} 项</span>
          <div className="flex-1" />
          <button
            onClick={bulkExport}
            className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-surface-muted"
          >
            <Download className="h-3.5 w-3.5" /> 导出选中
          </button>
          <button
            onClick={bulkReset}
            className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-surface-muted"
          >
            <Key className="h-3.5 w-3.5" /> 重置密码
          </button>
          <button
            onClick={bulkDelete}
            className="inline-flex items-center gap-1 rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100"
          >
            <Trash2 className="h-3.5 w-3.5" /> 删除
          </button>
          <button
            onClick={clearSelection}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-ink-400 hover:bg-white"
          >
            <X className="h-3.5 w-3.5" /> 取消选择
          </button>
        </div>
      )}

      {selected.size === 0 && users.length > 0 && (
        <div className="mb-3 flex justify-end">
          <button
            onClick={bulkExport}
            className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-surface-muted"
          >
            <Download className="h-3.5 w-3.5" /> 导出全部为 Excel
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-surface-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-[11px] uppercase tracking-wider text-ink-400">
            <tr>
              <th className="w-10 px-3 py-3 text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={allSelectableIds.length === 0}
                  aria-label="全选"
                />
              </th>
              <th className="px-5 py-3 text-left">用户名</th>
              <th className="px-5 py-3 text-left">姓名</th>
              <th className="px-5 py-3 text-left">角色</th>
              <th className="px-5 py-3 text-left">可见分类</th>
              <th className="px-5 py-3 text-left">创建时间</th>
              <th className="px-5 py-3 text-left">最近登录</th>
              <th className="px-5 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {loading ? (
              <tr><td colSpan={8} className="px-5 py-12 text-center text-ink-400">加载中…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={8} className="px-5 py-12 text-center text-ink-400">暂无用户</td></tr>
            ) : users.map((u) => {
              const isSelf = me?.id === u.id;
              return (
                <tr key={u.id} className={`hover:bg-surface-muted/40 ${selected.has(u.id) ? "bg-brand-light/30" : ""}`}>
                  <td className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleOne(u.id)}
                      disabled={isSelf}
                      aria-label={`选择 ${u.username}`}
                    />
                  </td>
                  <td className="px-5 py-3 font-medium text-ink-900">
                    {u.username}
                    {isSelf && <span className="ml-2 text-[10px] text-ink-400">（你自己）</span>}
                  </td>
                  <td className="px-5 py-3">
                    {editingName === u.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          className="input !py-1 !text-xs"
                          autoFocus
                          value={editingNameValue}
                          placeholder="Eric Xu"
                          onChange={(e) => setEditingNameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveDisplayName(u);
                            if (e.key === "Escape") setEditingName(null);
                          }}
                        />
                        <button
                          onClick={() => saveDisplayName(u)}
                          className="rounded-md bg-brand px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-hover"
                        >
                          保存
                        </button>
                        <button
                          onClick={() => setEditingName(null)}
                          className="rounded-md px-2 py-1 text-[11px] text-ink-400 hover:bg-surface-muted"
                        >
                          取消
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingName(u.id);
                          setEditingNameValue(u.displayName || "");
                        }}
                        className="group inline-flex items-center gap-1 rounded-md px-2 py-1 text-left hover:bg-surface-muted"
                        title="点击编辑姓名"
                      >
                        {u.displayName ? (
                          <span className="text-ink-700">{u.displayName}</span>
                        ) : (
                          <span className="italic text-ink-400">— 未填 —</span>
                        )}
                        <span className="text-[10px] text-ink-400 opacity-0 group-hover:opacity-100">✎</span>
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {u.is_admin ? (
                      <span className="badge !bg-accent-light !text-accent">管理员</span>
                    ) : (
                      <span className="text-ink-400">学员</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {u.is_admin ? (
                      <span className="text-ink-400">全部分类</span>
                    ) : (
                      <button
                        onClick={() => setAccessUser(u)}
                        className="group inline-flex max-w-[14rem] items-center gap-1 rounded-md px-2 py-1 text-left hover:bg-surface-muted"
                        title="点击设置可见分类"
                      >
                        {u.categoryIds.length === 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-500">
                            <FolderLock className="h-3 w-3" /> 无（看不到任何视频）
                          </span>
                        ) : (
                          <span className="truncate text-ink-700">
                            {u.categoryIds
                              .map((id) => categories.find((c) => c.id === id)?.name)
                              .filter(Boolean)
                              .join("、") || `${u.categoryIds.length} 个分类`}
                          </span>
                        )}
                        <span className="text-[10px] text-ink-400 opacity-0 group-hover:opacity-100">✎</span>
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-3 text-ink-400">{u.created_at?.slice(0, 16) || "—"}</td>
                  <td className="px-5 py-3 text-ink-400">{u.last_login_at?.slice(0, 16) || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => resetPwd(u)}
                      disabled={resetting === u.id}
                      className="mr-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-600 hover:bg-surface-muted"
                    >
                      <Key className="h-3 w-3" /> 重置密码
                    </button>
                    <button
                      onClick={() => del(u)}
                      disabled={isSelf}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Trash2 className="h-3 w-3" /> 删除
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {importOpen && (
        <ImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => {
            setImportOpen(false);
            load();
          }}
        />
      )}

      {accessUser && (
        <CategoryAccessModal
          user={accessUser}
          categories={categories}
          onClose={() => setAccessUser(null)}
          onSaved={() => {
            setAccessUser(null);
            load();
          }}
        />
      )}
    </>
  );
}

// ─────────────── Category access modal ───────────────

function CategoryAccessModal({
  user,
  categories,
  onClose,
  onSaved,
}: {
  user: ApiUser;
  categories: ApiCategory[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set(user.categoryIds));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const allChecked = categories.length > 0 && categories.every((c) => selected.has(c.id));

  function toggle(id: number) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(categories.map((c) => c.id)));
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await api.setUserCategories(user.id, Array.from(selected));
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-surface-border px-6 py-4">
          <div>
            <div className="text-base font-semibold text-ink-900">设置可见分类</div>
            <div className="mt-0.5 text-xs text-ink-400">
              用户：<b>{user.displayName || user.username}</b>
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-ink-400 hover:bg-surface-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
          {categories.length === 0 ? (
            <div className="py-8 text-center text-sm text-ink-400">还没有分类</div>
          ) : (
            <>
              <label className="mb-3 flex cursor-pointer items-center gap-2 border-b border-surface-border pb-2 text-sm font-medium text-ink-700">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                全选 / 全不选
              </label>
              <div className="space-y-1.5">
                {categories.map((c) => (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-surface-muted"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                    />
                    <span className="text-ink-700">{c.name}</span>
                  </label>
                ))}
              </div>
            </>
          )}
          {err && (
            <div className="mt-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4" /> {err}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-surface-border bg-surface-muted/30 px-6 py-3">
          <button onClick={onClose} className="btn-ghost">取消</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? "保存中…" : `保存（${selected.size} 个分类）`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────── Import modal ───────────────

interface ImportModalProps {
  onClose: () => void;
  onDone: () => void;
}

interface ParsedRow {
  row: number;
  username: string;
  password: string;
  displayName: string;
  isAdmin: boolean;
  warning?: string;
}

function ImportModal({ onClose, onDone }: ImportModalProps) {
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<{
    total: number;
    ok: number;
    failed: number;
    results: Array<{ row: number; username: string; ok: boolean; error?: string }>;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function downloadTemplate() {
    const ws = XLSX.utils.json_to_sheet([
      { 用户名: "zhangxun", 姓名: "章逊", 密码: "Welcome2026", 是否管理员: "否" },
      { 用户名: "eric", 姓名: "Eric Xu", 密码: "ChangeMe123", 是否管理员: "是" },
      { 用户名: "student-001", 姓名: "", 密码: "Mvg2026!", 是否管理员: "否" },
    ]);
    ws["!cols"] = [{ wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "用户");
    XLSX.writeFile(wb, "mvg-users-template.xlsx");
  }

  function pickFile(f: File | null) {
    if (!f) return;
    setErr(null);
    setParsed(null);
    setResults(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        if (!ws) throw new Error("工作表为空");
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: "" });
        if (rows.length === 0) throw new Error("表格里没有数据");
        if (rows.length > 1000) throw new Error("单次最多 1000 行");

        const out: ParsedRow[] = [];
        rows.forEach((r, i) => {
          const username = String(
            r["用户名"] ?? r["username"] ?? r["Username"] ?? ""
          ).trim();
          const password = String(r["密码"] ?? r["password"] ?? r["Password"] ?? "");
          const displayName = String(
            r["姓名"] ?? r["真实姓名"] ?? r["displayName"] ?? r["DisplayName"] ?? r["name"] ?? r["Name"] ?? ""
          ).trim();
          const adminCell = String(
            r["是否管理员"] ?? r["管理员"] ?? r["isAdmin"] ?? r["IsAdmin"] ?? ""
          ).trim().toLowerCase();
          const isAdmin = ["是", "yes", "true", "y", "1", "admin"].includes(adminCell);

          let warning: string | undefined;
          if (!username) warning = "用户名为空";
          else if (!password) warning = "密码为空";
          else if (password.length < 6) warning = "密码不足 6 位";

          out.push({ row: i + 2, username, password, displayName, isAdmin, warning });
        });
        setParsed(out);
      } catch (e) {
        setErr((e as Error).message);
      }
    };
    reader.readAsArrayBuffer(f);
  }

  async function doImport() {
    if (!parsed) return;
    const valid = parsed.filter((r) => !r.warning);
    if (valid.length === 0) { alert("没有有效行可导入"); return; }
    setImporting(true);
    try {
      const r = await api.bulkImportUsers(
        valid.map((x) => ({
          username: x.username,
          password: x.password,
          isAdmin: x.isAdmin,
          displayName: x.displayName || undefined,
        }))
      );
      setResults(r);
    } catch (e) { setErr((e as Error).message); }
    finally { setImporting(false); }
  }

  function downloadFailed() {
    if (!results) return;
    const failed = results.results.filter((x) => !x.ok);
    if (failed.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(
      failed.map((x) => ({ 行号: x.row, 用户名: x.username, 错误: x.error || "" }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "失败行");
    XLSX.writeFile(wb, "mvg-users-import-failed.xlsx");
  }

  const validCount = parsed ? parsed.filter((r) => !r.warning).length : 0;
  const invalidCount = parsed ? parsed.length - validCount : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-surface-border px-6 py-4">
          <div>
            <div className="text-base font-semibold text-ink-900">从 Excel 批量导入用户</div>
            <div className="mt-0.5 text-xs text-ink-400">支持 .xlsx / .xls / .csv，单次最多 1000 行</div>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-ink-400 hover:bg-surface-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
          {!results && (
            <>
              <div className="mb-4 flex items-center gap-3">
                <button
                  onClick={downloadTemplate}
                  className="inline-flex items-center gap-1.5 rounded-md border border-surface-border bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-surface-muted"
                >
                  <Download className="h-3.5 w-3.5" /> 下载模板
                </button>
                <span className="text-[11px] text-ink-400">
                  模板含 4 列：<b>用户名</b>、<b>姓名（可选）</b>、<b>密码</b>、<b>是否管理员</b>（填「是/否」或「yes/no」）
                </span>
              </div>

              <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-surface-border bg-surface-muted/40 px-6 py-10 hover:bg-surface-muted/70">
                <Upload className="h-6 w-6 text-ink-400" />
                <div className="text-sm font-medium text-ink-700">点击选择 Excel 文件</div>
                <div className="text-[11px] text-ink-400">.xlsx / .xls / .csv</div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => pickFile(e.target.files?.[0] || null)}
                />
              </label>

              {err && (
                <div className="mt-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4" /> {err}
                </div>
              )}

              {parsed && (
                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between text-xs">
                    <div>
                      <span className="font-semibold text-ink-900">共 {parsed.length} 行</span>
                      <span className="ml-3 text-emerald-600">✓ 有效 {validCount}</span>
                      {invalidCount > 0 && (
                        <span className="ml-3 text-amber-600">⚠ 不可导入 {invalidCount}</span>
                      )}
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto rounded-md border border-surface-border">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-surface-muted">
                        <tr>
                          <th className="px-3 py-2 text-left">行</th>
                          <th className="px-3 py-2 text-left">用户名</th>
                          <th className="px-3 py-2 text-left">姓名</th>
                          <th className="px-3 py-2 text-left">密码</th>
                          <th className="px-3 py-2 text-left">管理员</th>
                          <th className="px-3 py-2 text-left">状态</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-surface-border">
                        {parsed.map((r, i) => (
                          <tr key={i} className={r.warning ? "bg-amber-50" : ""}>
                            <td className="px-3 py-1.5 text-ink-400">{r.row}</td>
                            <td className="px-3 py-1.5 font-medium text-ink-900">{r.username || <i className="text-ink-400">—</i>}</td>
                            <td className="px-3 py-1.5 text-ink-700">{r.displayName || <i className="text-ink-400">—</i>}</td>
                            <td className="px-3 py-1.5 font-mono text-ink-600">
                              {r.password ? "•".repeat(Math.min(r.password.length, 12)) : <i className="text-ink-400">—</i>}
                            </td>
                            <td className="px-3 py-1.5">{r.isAdmin ? "是" : "否"}</td>
                            <td className="px-3 py-1.5">
                              {r.warning ? <span className="text-amber-700">⚠ {r.warning}</span> : <span className="text-emerald-600">✓ 可导入</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {results && (
            <div>
              <div className="mb-4 rounded-md border border-surface-border bg-surface-muted/40 px-4 py-3 text-sm">
                <div className="font-semibold text-ink-900">导入完成</div>
                <div className="mt-1 text-xs text-ink-600">
                  共 {results.total} 条 ·
                  <span className="ml-1 text-emerald-600">成功 {results.ok}</span> ·
                  <span className="ml-1 text-red-600">失败 {results.failed}</span>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-md border border-surface-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-surface-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">行</th>
                      <th className="px-3 py-2 text-left">用户名</th>
                      <th className="px-3 py-2 text-left">结果</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-border">
                    {results.results.map((r, i) => (
                      <tr key={i} className={r.ok ? "" : "bg-red-50"}>
                        <td className="px-3 py-1.5 text-ink-400">{r.row}</td>
                        <td className="px-3 py-1.5 font-medium text-ink-900">{r.username}</td>
                        <td className="px-3 py-1.5">
                          {r.ok ? <span className="text-emerald-600">✓ 已添加</span> : <span className="text-red-600">✗ {r.error}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-surface-border bg-surface-muted/30 px-6 py-3">
          {!results && parsed && (
            <button
              onClick={doImport}
              disabled={importing || validCount === 0}
              className="btn-primary disabled:opacity-50"
            >
              {importing ? "导入中…" : `导入 ${validCount} 条`}
            </button>
          )}
          {results && results.failed > 0 && (
            <button onClick={downloadFailed} className="btn-ghost">
              <Download className="h-3.5 w-3.5" /> 下载失败行
            </button>
          )}
          {results ? (
            <button onClick={onDone} className="btn-primary">完成</button>
          ) : (
            <button onClick={onClose} className="btn-ghost">取消</button>
          )}
        </div>
      </div>
    </div>
  );
}
